import React, { useEffect, useState } from 'react'
import { EmissionDate, ReportingTable, PageContainer, DeleteModal, SiteSelect } from './component/Table'
import { columnDefinitionFuelEnergyActivities } from './component/Table/column'
import { FiPlus, FiTrash } from 'react-icons/fi'
import { useLocation, useNavigate } from 'react-router-dom'
import useAxiosPrivate from '../../hooks/useAxiosPrivate'
import axios from 'axios'
import * as types from './reportData.json'

const ReportFuelEnergyActivities = () => {
  const column = columnDefinitionFuelEnergyActivities
  const navigate = useNavigate()
  const location = useLocation()
  const axiosPrivate = useAxiosPrivate()
  const { bypassModal } = location.state === null ? false : location.state
  const { fuel, malaysiaRegion } = types

  const [data, setData] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [showContentModal, setShowContentModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [modalMode, setModalMode] = useState(null) // 'add' or 'edit'
  const [editableData, setEditableData] = useState({
    emissionDate: '',
    reportBy: '',
    calculateBy: '',
    supplier: '',
    region: '',
    fuelType: '',
    fuelUnit: '',
    lossRateTD: 0,
    fuelConsumed: 0,
    purchasedFuelEF: 0,
    emissionCO2: 0,
    emissionCH4: 0,
    emissionN2O: 0,
    electricity: 0,
    electricityEF: 0,
    steam: 0,
    steamEF: 0,
    heating: 0,
    heatingEF: 0,
    cooling: 0,
    coolingEF: 0,
    description: '',
  })
  const [selectedRows, setSelectedRows] = useState(new Set())
  const [rowToDelete, setRowToDelete] = useState(null)

  useEffect(() => {
    let isMounted = true
    const controller = new AbortController()

    const fetchData = async () => {
      setIsLoading(true)
      try {
        const response = await axiosPrivate.get('fuelEnergyActivities/read-S3C-records', {
          signal: controller.signal,
        })

        if (isMounted) {
          // console.log(response.data);
          setData(response.data)
        }
      } catch (err) {
        if (axios.isCancel(err)) {
          // Log for debugging purposes
          // console.log('Request cancelled:', err.message);
        } else {
          console.error('Request failed:', err)
          // Only navigate if the error was not a cancellation
          // Check for a 403 status code specifically
          if (err.response && err.response.status === 403) {
            navigate('/sign-in', { state: { from: location }, replace: true })
          }
        }
      }

      setIsLoading(false)
    }

    fetchData()

    if (bypassModal === true) {
      handleAddNewClick()
    }

    return () => {
      isMounted = false
      controller.abort()
      // console.log('Cleanup: Cancelled any ongoing requests.');
    }
  }, [navigate, location, axiosPrivate])

  const handleAddNewClick = () => {
    setEditableData({
      emissionDate: '',
      reportBy: '',
      calculateBy: '',
      supplier: '',
      region: '',
      fuelType: '',
      fuelUnit: '',
      lossRateTD: 0,
      fuelConsumed: 0,
      purchasedFuelEF: 0,
      emissionCO2: 0,
      emissionCH4: 0,
      emissionN2O: 0,
      electricity: 0,
      electricityEF: 0,
      steam: 0,
      steamEF: 0,
      heating: 0,
      heatingEF: 0,
      cooling: 0,
      coolingEF: 0,
      description: '',
    })
    setModalMode('add')
    setShowContentModal(true)
  }

  const handleDeleteClick = () => {
    if (selectedRows.size > 0) {
      setShowDeleteModal(true)
    } else {
      alert('No rows selected') // Or show a more user-friendly message
    }
  }

  const requestRowDeletion = (rowId) => {
    setRowToDelete(rowId)
    setShowDeleteModal(true)
  }

  const confirmDelete = async () => {
    if (rowToDelete !== null) {
      let isMounted = true
      const controller = new AbortController()

      try {
        await axiosPrivate.delete(
          `/fuelEnergyActivities/delete-S3C-record/${rowToDelete}`,
          {
            headers: { 'Content-Type': 'application/json' },
            signal: controller.signal,
          },
        )

        if (isMounted) {
          // Update the state to reflect the deletion
          setData((prevData) =>
            prevData.filter((row) => row.id !== rowToDelete),
          )
          setRowToDelete(null)
        }
      } catch (err) {
        if (!axios.isCancel(err)) {
          console.error('Deletion failed:', err)
          if (err.response) {
            // Redirect on 403 Forbidden or show an error message
            if (err.response.status === 403) {
              navigate('/sign-in', {
                state: { from: location },
                replace: true,
              })
            } else {
              alert(
                `Error: ${err.response.data.message || 'An error occurred.'}`,
              )
            }
          }
        }
      } finally {
        if (isMounted) {
          setShowDeleteModal(false) // Close the modal upon completion or error
        }
      }

      // Cleanup function to prevent state updates if the component unmounts
      return () => {
        isMounted = false
        controller.abort()
      }
    } else {
      // If rowToDelete is null, just close the modal
      setShowDeleteModal(false)
    }
  }

  const deleteSelectedRows = async () => {
    // Check if any rows are selected for deletion
    if (selectedRows.size > 0) {
      let isMounted = true
      const controller = new AbortController()

      try {
        const recordIdsToDelete = Array.from(selectedRows)

        await axiosPrivate.delete('/fuelEnergyActivities/delete-S3C-records', {
          data: { ids: recordIdsToDelete }, // Sending user IDs in the request body
          headers: { 'Content-Type': 'application/json' },
          signal: controller.signal,
        })

        if (isMounted) {
          // Filter out the deleted users from the current data
          setData((prevData) =>
            prevData.filter((row) => !selectedRows.has(row.id)),
          )
          setSelectedRows(new Set()) // Clear selected rows after successful deletion
        }
      } catch (err) {
        if (!axios.isCancel(err)) {
          console.error('Deletion failed:', err)
          if (err.response) {
            // Handle specific HTTP status codes here
            if (err.response.status === 403) {
              navigate('/sign-in', {
                state: { from: location },
                replace: true,
              })
            } else {
              alert(
                `Error: ${err.response.data.message || 'An error occurred.'}`,
              )
            }
          }
        }
      } finally {
        if (isMounted) {
          setShowDeleteModal(false) // Close the modal upon completion or error
        }
      }

      // Cleanup function to prevent state updates if the component unmounts
      return () => {
        isMounted = false
        controller.abort()
      }
    } else {
      // If no rows were selected, just close the modal
      setShowDeleteModal(false)
    }
  }

  // Function to handle edit requests
  const handleEditClick = (rowData) => {
    setEditableData(rowData)
    setModalMode('edit')
    setShowContentModal(true)
  }

  const handleAddNewSubmit = async (event) => {
    event.preventDefault()

    const endpoint =
      modalMode === 'edit'
        ? `/fuelEnergyActivities/edit-S3C-record/${editableData.id}`
        : '/fuelEnergyActivities/create-S3C-record'
    const method = modalMode === 'edit' ? 'patch' : 'post'

    // Prepare the data payload
    const payload = {
      scope: 3,
      category: `investments`,
      emissionDate: editableData.emissionDate,
      reportBy: editableData.reportBy,
      calculateBy: editableData.calculateBy,
      supplier: editableData.supplier,
      region: editableData.region,
      fuelType: editableData.fuelType,
      fuelUnit: editableData.fuelUnit,
      lossRateTD: editableData.lossRateTD,
      fuelConsumed: editableData.fuelConsumed,
      purchasedFuelEF: editableData.purchasedFuelEF,
      emissionCO2: editableData.emissionCO2,
      emissionCH4: editableData.emissionCH4,
      emissionN2O: editableData.emissionN2O,
      electricity: editableData.electricity,
      electricityEF: editableData.electricityEF,
      steam: editableData.steam,
      steamEF: editableData.steamEF,
      heating: editableData.heating,
      heatingEF: editableData.heatingEF,
      cooling: editableData.cooling,
      coolingEF: editableData.coolingEF,
      description: editableData.description,
      siteId: editableData.siteId,
    }

    try {
      const response = await axiosPrivate[method](endpoint, payload, {
        headers: { 'Content-Type': 'application/json' },
      })

      // Update the state based on the modal mode
      if (modalMode === 'edit') {
        setData((currentData) =>
          currentData.map((item) =>
            item.id === editableData.id ? { ...item, ...response.data } : item,
          ),
        )
      } else {
        setData((currentData) => [...currentData, response.data])
      }

      // Reset form and close modal
      setEditableData({
        emissionDate: '',
        reportBy: '',
        calculateBy: '',
        supplier: '',
        region: '',
        fuelType: '',
        fuelUnit: '',
        lossRateTD: 0,
        fuelConsumed: 0,
        purchasedFuelEF: 0,
        emissionCO2: 0,
        emissionCH4: 0,
        emissionN2O: 0,
        electricity: 0,
        electricityEF: 0,
        steam: 0,
        steamEF: 0,
        heating: 0,
        heatingEF: 0,
        cooling: 0,
        coolingEF: 0,
        description: '',
      })
      setShowContentModal(false)
    } catch (err) {
      if (!axios.isCancel(err)) {
        console.error('Submission failed:', err)
        const errorMsg = err.response?.data.message || 'An error occurred.'
        if (err.response?.status === 403) {
          navigate('/sign-in', { state: { from: location }, replace: true })
        } else {
          alert(`Error: ${errorMsg}`)
        }
      }
    }
  }

  if (isLoading) {
    return <div>Loading...</div> // Or any loading spinner
  }

  return (
    <PageContainer
      title="Fuel and Energy Related Activities Reporting"
      onAdd={handleAddNewClick}
      onDelete={handleDeleteClick}
    >
      {/* {showContentModal && <MyModal onClose={() => setShowContentModal(false)} />} */}
      <ReportingTable
        data={data}
        columns={column}
        selectedRows={selectedRows}
        setSelectedRows={setSelectedRows}
        onRequestDelete={requestRowDeletion}
        onEdit={handleEditClick}
      />
      {showDeleteModal && (
        <DeleteModal
        show={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={(row) => (row !== null ? confirmDelete() : deleteSelectedRows())}
        rowToDelete={rowToDelete}
      />
      )}

      {showContentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-md flex justify-center items-center p-4">
          <div className="bg-white dark:bg-gray-800 w-full max-w-lg mx-auto rounded shadow-lg overflow-hidden p-6 max-h-[90vh] overflow-y-auto">
            <h2 className="text-lg font-semibold mb-4">
              {modalMode === 'edit' ? 'Edit Data' : 'Add New Data'}
            </h2>
            <form onSubmit={handleAddNewSubmit}>
              {/* Emission Date Input */}
              <EmissionDate data={editableData} setEditableData = {setEditableData}/>

              {/* Site Select */}
              <SiteSelect 
                 value={editableData.siteId} 
                 onChange={(val) => setEditableData({ ...editableData, siteId: val })} 
                 required 
              />

              {/* ReportBy Dropdown */}
              <div className="mb-4">
                <label className="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                  Report By
                </label>
                <select
                  className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white"
                  value={editableData.reportBy}
                  onChange={(e) =>
                    setEditableData({
                      ...editableData,
                      reportBy: e.target.value,
                      calculateBy: '',
                      supplier: '',
                      region: '',
                      fuelType: '',
                      fuelUnit: '',
                      lossRateTD: 0,
                      fuelConsumed: 0,
                      purchasedFuelEF: 0,
                      emissionCO2: 0,
                      emissionCH4: 0,
                      emissionN2O: 0,
                      electricity: 0,
                      electricityEF: 0,
                      steam: 0,
                      steamEF: 0,
                      heating: 0,
                      heatingEF: 0,
                      cooling: 0,
                      coolingEF: 0,
                    })
                  }
                  required
                >
                  <option value="">Select Type</option>
                  <option value="Upstream Emissions of Purchased Fuels">
                    Upstream Emissions of Purchased Fuels
                  </option>
                  <option value="Upstream Emissions of Purchased Electricity">
                    Upstream Emissions of Purchased Electricity
                  </option>
                  <option value="Transmission and Distribution Losses">
                    Transmission and Distribution Losses
                  </option>
                  <option value="Generation of Purchased Electricity that is Sold to End Users">
                    Generation of Purchased Electricity that is Sold to End Users
                  </option>
                </select>
              </div>

              {/* CalculateBy Dropdown */}
              {editableData.reportBy && (
                <div className="mb-4">
                  <label className="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                    Calculate By
                  </label>
                  <select
                    className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white"
                    value={editableData.calculateBy}
                    onChange={(e) =>
                      setEditableData({
                        ...editableData,
                        calculateBy: e.target.value,
                        supplier: '',
                        region: '',
                        fuelType: '',
                        fuelUnit: '',
                        lossRateTD: 0,
                        fuelConsumed: 0,
                        purchasedFuelEF: 0,
                        emissionCO2: 0,
                        emissionCH4: 0,
                        emissionN2O: 0,
                        electricity: 0,
                        electricityEF: 0,
                        steam: 0,
                        steamEF: 0,
                        heating: 0,
                        heatingEF: 0,
                        cooling: 0,
                        coolingEF: 0,
                      })
                    }
                    required
                  >
                    <option value="">Select Type</option>
                    <option value="Supplier-Specific Method">Supplier-Specific Method</option>
                    <option value="Average-Data Method">Average-Data Method</option>
                  </select>
                </div>
              )}

              {/* Supplier Input */}
              {editableData.reportBy && (
                <div className="mb-4">
                  <label className="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                    Supplier
                  </label>
                  <input
                    type="text"
                    className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white"
                    value={editableData.supplier}
                    onChange={(e) =>
                      setEditableData({
                        ...editableData,
                        supplier: e.target.value,
                      })
                    }
                    required
                  />
                </div>
              )}

              {/* Country/Region Input */}
              {editableData.reportBy && editableData.reportBy !== "Upstream Emissions of Purchased Fuels" && (
                <div className="mb-4">
                  <label className="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                    Country/Region
                  </label>
                  <select
                  className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white"
                  value={editableData.region}
                  onChange={(e) =>
                    setEditableData({
                      ...editableData,
                      region: e.target.value
                    })
                  }
                  required
                >
                  <option value="">Select Type</option>
                  {malaysiaRegion.map((region) => (
                      <option key={region} value={region}>{region}</option>
                    ))}
                </select>
                </div>
              )}

              {/* CO2 Emission Input*/}
              {(editableData.reportBy === "Generation of Purchased Electricity that is Sold to End Users" && editableData.calculateBy === "Supplier-Specific Method") && (
                <div className="mb-4">
                  <label className="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                    Specific CO2 Emission from Purchased Energy is produced (kg CO2)
                    </label>
                  <input
                    type="number"
                    onWheel={(e) => e.target.blur()}
                    className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white"
                    value={editableData.emissionCO2}
                    onChange={(e) =>
                      setEditableData({
                        ...editableData,
                        emissionCO2: e.target.value,
                      })
                    }
                    required
                  />
                </div>
              )}

              {/* CO2 Emission Input*/}
              {(editableData.reportBy === "Generation of Purchased Electricity that is Sold to End Users" && editableData.calculateBy === "Supplier-Specific Method") && (
                <div className="mb-4">
                  <label className="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                    Specific CH4 Emission from Purchased Energy is produced (kg CH4)
                    </label>
                  <input
                    type="number"
                    onWheel={(e) => e.target.blur()}
                    className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white"
                    value={editableData.emissionCH4}
                    onChange={(e) =>
                      setEditableData({
                        ...editableData,
                        emissionCH4: e.target.value,
                      })
                    }
                    required
                  />
                </div>
              )}

              {/* CO2 Emission Input*/}
              {(editableData.reportBy === "Generation of Purchased Electricity that is Sold to End Users" && editableData.calculateBy === "Supplier-Specific Method") && (
                <div className="mb-4">
                  <label className="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                    Specific N2O Emission from Purchased Energy is produced (kg N2O)
                    </label>
                  <input
                    type="number"
                    onWheel={(e) => e.target.blur()}
                    className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white"
                    value={editableData.emissionN2O}
                    onChange={(e) =>
                      setEditableData({
                        ...editableData,
                        emissionN2O: e.target.value,
                      })
                    }
                    required
                  />
                </div>
              )}

              {/* Fuel Type Input */}
              {editableData.reportBy === "Upstream Emissions of Purchased Fuels" && (
                <div className="mb-4">
                  <label className="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                    Fuel Type
                  </label>
                  <select
                  className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white"
                  value={editableData.fuelType}
                  onChange={(e) =>
                    setEditableData({
                      ...editableData,
                      fuelType: e.target.value
                    })
                  }
                  required
                >
                  <option value="">Select Type</option>
                  {fuel.map((type) => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
                </div>
              )}

              {/* Fuel Unit Input */}
              {editableData.reportBy === "Upstream Emissions of Purchased Fuels" && (
                <div className="mb-4">
                  <label className="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                    Fuel Unit
                  </label>
                  <select
                  className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white"
                  value={editableData.fuelUnit}
                  onChange={(e) =>
                    setEditableData({
                      ...editableData,
                      fuelUnit: e.target.value
                    })
                  }
                  required
                >
                  <option value="">Select Type</option>
                  <option value="Kilogram">Kilogram</option>
                  <option value="Litres">Litres</option>
                  <option value="m^3">m^3</option>
                  <option value="kWh">kWh</option>
                </select>
                </div>
              )}

              {/* Fuel Amount Input*/}
              {(editableData.reportBy === "Upstream Emissions of Purchased Fuels") && (
                <div className="mb-4">
                  <label className="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                    Amount of Fuel
                    </label>
                  <input
                    type="number"
                    onWheel={(e) => e.target.blur()}
                    className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white"
                    value={editableData.fuelConsumed}
                    onChange={(e) =>
                      setEditableData({
                        ...editableData,
                        fuelConsumed: e.target.value,
                      })
                    }
                    required
                  />
                </div>
              )}

              {/* Fuel EF Input*/}
              {(editableData.reportBy === "Upstream Emissions of Purchased Fuels" && editableData.calculateBy) && (
                <div className="mb-4">
                  <label className="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                    {editableData.calculateBy === "Supplier-Specific Method" ? "Fuel-Provider-Specific Emission Factors for Extraction, Production and Transportation (kg CO2e / unit fuel)" : "Average Fuel Emission Factors for Upstream Activities (Extraction, Production and Transportation) (kg CO2e / unit fuel)"}
                    </label>
                  <input
                    type="number"
                    onWheel={(e) => e.target.blur()}
                    className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white"
                    value={editableData.purchasedFuelEF}
                    onChange={(e) =>
                      setEditableData({
                        ...editableData,
                        purchasedFuelEF: e.target.value,
                      })
                    }
                    required
                  />
                </div>
              )}

              {/* T&D Loss Rate Input*/}
              {editableData.reportBy === "Transmission and Distribution Losses" && editableData.calculateBy && (
                <div className="mb-4">
                  <label className="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                      {(editableData.calculateBy === "Supplier-Specific Method") ? "Utility(Grid)-Specific T&D Loss Rate (%)" : "Average (Country, Regional, Global) T&D Loss Rate (%)"}
                    </label>
                  <input
                    type="number"
                    onWheel={(e) => e.target.blur()}
                    className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white"
                    value={editableData.lossRateTD}
                    onChange={(e) =>
                      setEditableData({
                        ...editableData,
                        lossRateTD: e.target.value,
                      })
                    }
                    required
                  />
                </div>
              )}

              {/* Electricity Input*/}
              {editableData.calculateBy && (editableData.reportBy === "Upstream Emissions of Purchased Electricity" || editableData.reportBy === "Transmission and Distribution Losses" || (editableData.reportBy === "Generation of Purchased Electricity that is Sold to End Users" && editableData.calculateBy === "Average-Data Method")) && (
                <div className="mb-4">
                  <label className="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                      {(editableData.reportBy === "Upstream Emissions of Purchased Electricity" || editableData.reportBy === "Transmission and Distribution Losses") ? "Electricity Consumption (kWh)" : "Electricity Purchased for Resale (kWh)"}
                    </label>
                  <input
                    type="number"
                    onWheel={(e) => e.target.blur()}
                    className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white"
                    value={editableData.electricity}
                    onChange={(e) =>
                      setEditableData({
                        ...editableData,
                        electricity: e.target.value,
                      })
                    }
                  />
                </div>
              )}

              {/* Electricity EF Input*/}
              {editableData.calculateBy && (editableData.reportBy === "Upstream Emissions of Purchased Electricity" || editableData.reportBy === "Transmission and Distribution Losses" || (editableData.reportBy === "Generation of Purchased Electricity that is Sold to End Users" && editableData.calculateBy === "Average-Data Method")) && (
                <div className="mb-4">
                  <label className="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                    {editableData.reportBy === "Upstream Emissions of Purchased Electricity" && editableData.calculateBy === "Supplier-Specific Method" ? "Supplier Upstream Electricity Emission Factor (kg CO2e / kWh)" : null}    
                    {editableData.reportBy === "Upstream Emissions of Purchased Electricity" && editableData.calculateBy === "Average-Data Method" ? "Average Upstream Electricity Emission Factor (kg CO2e / kWh)" : null}    
                    {(editableData.reportBy === "Generation of Purchased Electricity that is Sold to End Users" || editableData.reportBy === "Transmission and Distribution Losses") ? "Electricity Life Cycle Emission Factor (kg CO2e / kWh)" : null}    
                  </label>
                  <input
                    type="number"
                    onWheel={(e) => e.target.blur()}
                    className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white"
                    value={editableData.electricityEF}
                    onChange={(e) =>
                      setEditableData({
                        ...editableData,
                        electricityEF: e.target.value,
                      })
                    }
                  />
                </div>
              )}

              {/* Steam Input*/}
              {editableData.calculateBy && (editableData.reportBy === "Upstream Emissions of Purchased Electricity" || editableData.reportBy === "Transmission and Distribution Losses" || (editableData.reportBy === "Generation of Purchased Electricity that is Sold to End Users" && editableData.calculateBy === "Average-Data Method")) && (
                <div className="mb-4">
                  <label className="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                      {(editableData.reportBy === "Upstream Emissions of Purchased Electricity" || editableData.reportBy === "Transmission and Distribution Losses") ? "Steam Consumption (kWh)" : "Steam Purchased for Resale (kWh)"}
                    </label>
                  <input
                    type="number"
                    onWheel={(e) => e.target.blur()}
                    className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white"
                    value={editableData.steam}
                    onChange={(e) =>
                      setEditableData({
                        ...editableData,
                        steam: e.target.value,
                      })
                    }
                  />
                </div>
              )}

              {/* Steam EF Input*/}
              {editableData.calculateBy && (editableData.reportBy === "Upstream Emissions of Purchased Electricity" || editableData.reportBy === "Transmission and Distribution Losses" || (editableData.reportBy === "Generation of Purchased Electricity that is Sold to End Users" && editableData.calculateBy === "Average-Data Method")) && (
                <div className="mb-4">
                  <label className="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                    {editableData.reportBy === "Upstream Emissions of Purchased Electricity" && editableData.calculateBy === "Supplier-Specific Method" ? "Supplier Upstream Steam Emission Factor (kg CO2e / kWh)" : null}    
                    {editableData.reportBy === "Upstream Emissions of Purchased Electricity" && editableData.calculateBy === "Average-Data Method" ? "Average Upstream Steam Emission Factor (kg CO2e / kWh)" : null}    
                    {(editableData.reportBy === "Generation of Purchased Electricity that is Sold to End Users" || editableData.reportBy === "Transmission and Distribution Losses") ? "Steam Life Cycle Emission Factor (kg CO2e / kWh)" : null}    
                  </label>
                  <input
                    type="number"
                    onWheel={(e) => e.target.blur()}
                    className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white"
                    value={editableData.steamEF}
                    onChange={(e) =>
                      setEditableData({
                        ...editableData,
                        steamEF: e.target.value,
                      })
                    }
                  />
                </div>
              )}

              {/* Heating Input*/}
              {editableData.calculateBy && (editableData.reportBy === "Upstream Emissions of Purchased Electricity" || editableData.reportBy === "Transmission and Distribution Losses" || (editableData.reportBy === "Generation of Purchased Electricity that is Sold to End Users" && editableData.calculateBy === "Average-Data Method")) && (
                <div className="mb-4">
                  <label className="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                      {(editableData.reportBy === "Upstream Emissions of Purchased Electricity" || editableData.reportBy === "Transmission and Distribution Losses") ? "Heating Consumption (kWh)" : "Heating Purchased for Resale (kWh)"}
                    </label>
                  <input
                    type="number"
                    onWheel={(e) => e.target.blur()}
                    className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white"
                    value={editableData.heating}
                    onChange={(e) =>
                      setEditableData({
                        ...editableData,
                        heating: e.target.value,
                      })
                    }
                  />
                </div>
              )}

              {/* Heating EF Input*/}
              {editableData.calculateBy && (editableData.reportBy === "Upstream Emissions of Purchased Electricity" || editableData.reportBy === "Transmission and Distribution Losses" || (editableData.reportBy === "Generation of Purchased Electricity that is Sold to End Users" && editableData.calculateBy === "Average-Data Method")) && (
                <div className="mb-4">
                  <label className="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                    {editableData.reportBy === "Upstream Emissions of Purchased Electricity" && editableData.calculateBy === "Supplier-Specific Method" ? "Supplier Upstream Heating Emission Factor (kg CO2e / kWh)" : null}    
                    {editableData.reportBy === "Upstream Emissions of Purchased Electricity" && editableData.calculateBy === "Average-Data Method" ? "Average Upstream Heating Emission Factor (kg CO2e / kWh)" : null}    
                    {(editableData.reportBy === "Generation of Purchased Electricity that is Sold to End Users" || editableData.reportBy === "Transmission and Distribution Losses") ? "Heating Life Cycle Emission Factor (kg CO2e / kWh)" : null}    
                  </label>
                  <input
                    type="number"
                    onWheel={(e) => e.target.blur()}
                    className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white"
                    value={editableData.heatingEF}
                    onChange={(e) =>
                      setEditableData({
                        ...editableData,
                        heatingEF: e.target.value,
                      })
                    }
                  />
                </div>
              )}

              {/* Cooling Input*/}
              {editableData.calculateBy && (editableData.reportBy === "Upstream Emissions of Purchased Electricity" || editableData.reportBy === "Transmission and Distribution Losses" || (editableData.reportBy === "Generation of Purchased Electricity that is Sold to End Users" && editableData.calculateBy === "Average-Data Method")) && (
                <div className="mb-4">
                  <label className="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                      {(editableData.reportBy === "Upstream Emissions of Purchased Electricity" || editableData.reportBy === "Transmission and Distribution Losses") ? "Cooling Consumption (kWh)" : "Cooling Purchased for Resale (kWh)"}
                    </label>
                  <input
                    type="number"
                    onWheel={(e) => e.target.blur()}
                    className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white"
                    value={editableData.cooling}
                    onChange={(e) =>
                      setEditableData({
                        ...editableData,
                        cooling: e.target.value,
                      })
                    }
                  />
                </div>
              )}

              {/* Cooling EF Input*/}
              {editableData.calculateBy && (editableData.reportBy === "Upstream Emissions of Purchased Electricity" || editableData.reportBy === "Transmission and Distribution Losses" || (editableData.reportBy === "Generation of Purchased Electricity that is Sold to End Users" && editableData.calculateBy === "Average-Data Method")) && (
                <div className="mb-4">
                  <label className="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                    {editableData.reportBy === "Upstream Emissions of Purchased Electricity" && editableData.calculateBy === "Supplier-Specific Method" ? "Supplier Upstream Cooling Emission Factor (kg CO2e / kWh)" : null}    
                    {editableData.reportBy === "Upstream Emissions of Purchased Electricity" && editableData.calculateBy === "Average-Data Method" ? "Average Upstream Cooling Emission Factor (kg CO2e / kWh)" : null}    
                    {(editableData.reportBy === "Generation of Purchased Electricity that is Sold to End Users" || editableData.reportBy === "Transmission and Distribution Losses") ? "Cooling Life Cycle Emission Factor (kg CO2e / kWh)" : null}    
                  </label>
                  <input
                    type="number"
                    onWheel={(e) => e.target.blur()}
                    className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white"
                    value={editableData.coolingEF}
                    onChange={(e) =>
                      setEditableData({
                        ...editableData,
                        coolingEF: e.target.value,
                      })
                    }
                  />
                </div>
              )}

              
              {/* Description Input */}
              <div className="mb-4">
                <label className="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                  Description
                </label>
                <textarea
                  className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white"
                  value={editableData.description}
                  onChange={(e) =>
                    setEditableData({
                      ...editableData,
                      description: e.target.value,
                    })
                  }
                  required
                />
              </div>

              <div className="flex justify-end mt-4">
                <button
                  type="button"
                  onClick={() => setShowContentModal(false)}
                  className="bg-gray-300 dark:bg-gray-600 dark:text-gray-200 px-4 py-2 rounded mr-2"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="bg-blue-500 text-white px-4 py-2 rounded"
                >
                  Submit
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </PageContainer>
  )
}

export default ReportFuelEnergyActivities